/**
 * 長くお使いでない方（休眠の棚卸し・人・issue #96・docs/01 5-9）。
 *
 * この画面でできること:
 *  - 決めた日数より長くログインしていない方の一覧を見る（週に一度・月曜に新しくなる）
 *  - 確かめたうえで、選んだ方をまとめて無効にする
 *
 * 決めごと（docs/06 決定22・5-2「見込み → 確認 → 実行」）:
 *  - **自動では何も止まりません。** 一覧を見ただけでは、どなたも無効になりません。
 *  - 押す前に、確認の窓で人数とお名前・元に戻せることを出します。終わったら、できた人数と、
 *    できなかった方の理由を結果の帯で出します。無効にしたことは1人ずつ操作履歴に残ります。
 *  - **無効にできない方（ほかの管理者・ご自身が持っていないできることを含む役職の方など）は、
 *    選べない行にして理由を出します**（押せるのに必ず断られる選び方をさせない）。
 *    判定はサーバの門番が行い、この画面はその答えを出すだけです。
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, nextStepAfter, type LoadFailure } from '../api/load-failure.js';
import type { BulkOutcome, DormantMember, DormantMemberList } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { BulkSkippedList } from '../components/BulkSkippedList.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { count, fullDate, windowSentence } from '../dormancy/labels.js';
import { withReason } from '../format/sentence.js';
import { memberStatusLabel } from '../members/labels.js';
import { can } from '../menu/visible.js';

/** 確認の窓に名前を並べる数（多いときは「ほか◯人」とまとめる）。 */
const NAMES_IN_DIALOG = 10;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep?: ReactNode;
}

export function DormantMembersPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const canChangeSettings = profile?.grant ? can(profile.grant, 'settings.manage') : false;

  const [data, setData] = useState<DormantMemberList | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<DormantMemberList>('/admin/dormancy/members')
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        // 読み直した一覧に居なくなった方の選択は外す（無効にした方・一覧から外れた方）
        setSelected((current) => {
          const still = new Set(loaded.items.map((row) => row.id));
          return new Set([...current].filter((id) => still.has(id)));
        });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const selectable = useMemo(
    () => (data?.items ?? []).filter((row) => row.blocked_reason === null),
    [data],
  );
  const chosen = useMemo(
    () => (data?.items ?? []).filter((row) => selected.has(row.id)),
    [data, selected],
  );

  const toggle = useCallback((id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  async function run(): Promise<void> {
    const ids = chosen.map((row) => row.id);
    setBusy(true);
    try {
      const outcome = await api.post<BulkOutcome>('/admin/dormancy/members/deactivate', {
        body: { user_ids: ids },
      });
      setConfirming(false);
      setResult(bannerOf(outcome, new Map(chosen.map((row) => [row.id, row.display_name]))));
      setSelected(new Set());
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setConfirming(false);
      setResult({
        tone: 'error',
        message: withReason(
          '無効にできませんでした',
          caught instanceof ApiError ? caught.message : messageForStatus(0),
        ),
        nextStep: nextStepAfter(caught, '少し時間をおいて、もう一度お試しください。'),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="長くお使いでない方"
        description="決めた日数より長くログインしていない方の一覧です。退職の連絡が漏れた方がいないか確かめ、選んだ方をまとめて無効にできます。一覧に出ても、自動では何も止まりません。"
      />

      {result ? (
        <ResultBanner
          tone={result.tone}
          message={result.message}
          {...(result.nextStep !== undefined ? { nextStep: result.nextStep } : {})}
          reveal
        />
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock label="一覧を読み込んでいます…" /> : null}

      {data ? (
        <>
          <p className="admin-note">
            {windowSentence(
              data,
              'ログインしていない方（メンバーとして登録されてから一度もログインしていない方を含みます）',
            )}
            {canChangeSettings ? (
              <>
                日数は<Link to="/settings/dormancy">会社の設定</Link>で変えられます。
              </>
            ) : null}
          </p>
          {data.items.length === 0 ? (
            <EmptyBlock label="いまは、長くお使いでない方はいません。" />
          ) : (
            <>
              {data.total > data.items.length ? (
                <p className="admin-note">
                  全部で{count(data.total)}名います。いまは、長くお使いでない順に
                  {count(data.items.length)}
                  名を出しています。無効にすると、次の方が出ます。
                </p>
              ) : null}
              <div className="admin-bulk-bar" role="group" aria-label="まとめて無効にする">
                <span className="admin-bulk-count" aria-live="polite">
                  {chosen.length === 0
                    ? 'まだ誰も選んでいません。'
                    : `${count(chosen.length)}名を選んでいます。`}
                </span>
                <Button
                  variant="secondary"
                  compact
                  disabled={selectable.length === 0}
                  onClick={() => setSelected(new Set(selectable.map((row) => row.id)))}
                >
                  選べる方をすべて選ぶ
                </Button>
                <Button
                  variant="quiet"
                  compact
                  disabled={chosen.length === 0}
                  onClick={() => setSelected(new Set())}
                >
                  選ぶのをやめる
                </Button>
                <Button
                  variant="danger"
                  disabled={chosen.length === 0}
                  onClick={() => {
                    setResult(null);
                    setConfirming(true);
                  }}
                >
                  選んだ方を無効にする
                </Button>
              </div>
              <DataTable label="長くお使いでない方の一覧">
                <thead>
                  <tr>
                    <th scope="col">選ぶ</th>
                    <th scope="col">お名前</th>
                    <th scope="col">拠点</th>
                    <th scope="col">役職</th>
                    <th scope="col">状態</th>
                    <th scope="col">最後にログインした日</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <MemberRow
                      key={row.id}
                      row={row}
                      checked={selected.has(row.id)}
                      onToggle={toggle}
                    />
                  ))}
                </tbody>
              </DataTable>
            </>
          )}
        </>
      ) : null}

      <ConfirmDialog
        open={confirming}
        title={`${count(chosen.length)}名のアカウントを無効にします`}
        message={
          <>
            {namesOf(chosen)}
            を無効にします。ログインできなくなり、通知も届かなくなります。お使いの端末からもログアウトされます。
          </>
        }
        reversibility="あとから「メンバー」の画面で1人ずつ無効を解除できます（解除した方は、ご本人がもう一度ログインするところから始めます）。無効にしたことは操作履歴に残ります。"
        reversible
        confirmLabel="無効にする"
        busy={busy}
        onConfirm={() => void run()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}

function MemberRow({
  row,
  checked,
  onToggle,
}: {
  row: DormantMember;
  checked: boolean;
  onToggle: (id: string, on: boolean) => void;
}): ReactNode {
  const blocked = row.blocked_reason !== null;
  return (
    <tr>
      <td>
        <label className="ui-check" htmlFor={`dormant-member-${row.id}`}>
          <input
            id={`dormant-member-${row.id}`}
            type="checkbox"
            checked={checked}
            disabled={blocked}
            onChange={(event) => onToggle(row.id, event.target.checked)}
          />
          <span className="admin-visually-hidden">{row.display_name}さんを選ぶ</span>
        </label>
      </td>
      <th scope="row" className="ui-cell-wrap">
        <Link to={`/members/${row.id}`}>{row.display_name}</Link>
        {blocked ? <small className="admin-row-reason">{row.blocked_reason}</small> : null}
      </th>
      <td className="ui-cell-soft">{row.site_name ?? '拠点なし'}</td>
      <td>{row.role_display_name}</td>
      <td>{memberStatusLabel(row.status)}</td>
      <td className="ui-cell-date">
        {row.last_active_at === null
          ? `一度もありません（メンバーとして登録された日: ${fullDate(row.created_at)}）`
          : fullDate(row.last_active_at)}
      </td>
    </tr>
  );
}

/** 確認の窓に出すお名前（多いときは「ほか◯人」）。 */
function namesOf(rows: DormantMember[]): string {
  const shown = rows.slice(0, NAMES_IN_DIALOG).map((row) => `${row.display_name}さん`);
  const rest = rows.length - shown.length;
  return `${shown.join('、')}${rest > 0 ? `、ほか${count(rest)}名` : ''}`;
}

/** まとめて実行した結果の帯。できなかった方がいれば、お名前と理由を並べる。 */
function bannerOf(outcome: BulkOutcome, knownNames: ReadonlyMap<string, string>): Banner {
  const done = outcome.done.length;
  const skipped = outcome.skipped.length;
  if (skipped === 0) {
    return {
      tone: 'success',
      message: `${count(done)}名を無効にしました。`,
      nextStep:
        '無効にしたことは操作履歴に残りました。戻すときは「メンバー」の画面から無効を解除できます。',
    };
  }
  return {
    tone: done === 0 ? 'error' : 'success',
    message:
      done === 0
        ? `選んだ${count(skipped)}名は無効にしませんでした。`
        : `${count(done)}名を無効にしました。${count(skipped)}名は無効にしませんでした。`,
    nextStep: <BulkSkippedList skipped={outcome.skipped} suffix="さん" knownNames={knownNames} />,
  };
}
